import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import ResponsiblesService from '../../services/api/responsibles';
import styles from './BankAccountModal.module.scss';

/**
 * 🏦 BankAccountModal - Modal Moderno para Cadastro/Edição de Contas Bancárias
 * 
 * Features:
 * - Design moderno com CSS Modules (padrão TAGs/Tickets)
 * - Sem bordas vermelhas de validação inline
 * - Validação apenas no submit
 * - Suporte a dark/light mode
 * - Integrado com sistema de aprovação
 */

const BankAccountModal = ({
  show,
  onHide,
  editingAccount = null,
  banks = [],
  responsibles = [],
  onSubmit,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    responsible_id: '',
    bank_name: '',
    bank_code: '',
    account_type: 'corrente',
    agency: '',
    account_number: '',
    account_digit: '',
    pix_key: '',
    pix_type: '',
    holder_name: '',
    holder_document: ''
  });

  const [error, setError] = useState('');

  // Inicializar formulário ao abrir modal
  useEffect(() => {
    if (show) {
      if (editingAccount) {
        // Modo edição
        setFormData({
          responsible_id: editingAccount.responsible_id?.toString() || '',
          bank_name: editingAccount.bank_name || '',
          bank_code: editingAccount.bank_code || '',
          account_type: editingAccount.account_type || 'corrente',
          agency: editingAccount.agency || '',
          account_number: editingAccount.account_number || '',
          account_digit: editingAccount.account_digit || '',
          pix_key: editingAccount.pix_key || '',
          pix_type: editingAccount.pix_type || '',
          holder_name: editingAccount.holder_name || '',
          holder_document: editingAccount.holder_document || ''
        });
      } else {
        // Modo criação - pré-selecionar responsável principal
        const mainResponsible = responsibles.find(r => r.is_main_responsible);
        // Para PF: usar admin_name como holder_name
        // Para PJ: usar company_name ou trading_name
        const holderName = mainResponsible
          ? (mainResponsible.company_name || mainResponsible.trading_name || mainResponsible.admin_name || '')
          : '';
        setFormData({
          responsible_id: mainResponsible ? mainResponsible.id.toString() : '',
          bank_name: '',
          bank_code: '',
          account_type: 'corrente',
          agency: '',
          account_number: '',
          account_digit: '',
          pix_key: '',
          pix_type: '',
          holder_name: holderName,
          holder_document: mainResponsible ? mainResponsible.responsible_cpf : ''
        });
      }
      setError('');
    }
  }, [show, editingAccount, responsibles]);

  // Função para permitir apenas números
  const onlyNumbers = (value) => value.replace(/\D/g, '');

  // Função para permitir números e X (para dígito verificador)
  const onlyNumbersOrX = (value) => value.replace(/[^0-9Xx]/g, '').toUpperCase();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Limpar erro ao digitar
  };

  // Handler específico para campos bancários com validação
  const handleBankFieldChange = (field, value, maxLength, allowX = false) => {
    const sanitized = allowX ? onlyNumbersOrX(value) : onlyNumbers(value);
    const truncated = sanitized.slice(0, maxLength);
    setFormData(prev => ({ ...prev, [field]: truncated }));
    setError('');
  };

  const handleResponsibleChange = (responsibleId) => {
    const responsible = responsibles.find(r => r.id.toString() === responsibleId);
    
    // Usar admin_name como fallback se não tiver company_name nem trading_name
    const holderName = responsible 
      ? (responsible.company_name || responsible.trading_name || responsible.admin_name || '')
      : '';
    
    setFormData(prev => ({
      ...prev,
      responsible_id: responsibleId,
      holder_name: holderName,
      holder_document: responsible ? responsible.responsible_cpf : ''
    }));
    setError('');
  };

  const handleBankChange = (bankCode) => {
    const bank = banks.find(b => b.code === bankCode);
    setFormData(prev => ({
      ...prev,
      bank_code: bankCode,
      bank_name: bank ? bank.name : ''
    }));
    setError('');
  };

  const validateForm = () => {
    // Validar campos obrigatórios
    if (!formData.responsible_id) {
      setError('Selecione um responsável');
      return false;
    }
    if (!formData.bank_code) {
      setError('Selecione um banco');
      return false;
    }
    if (!formData.agency) {
      setError('Digite a agência');
      return false;
    }
    // Validar limite da agência (máximo 4 dígitos)
    if (formData.agency.length > 4) {
      setError('Agência deve ter no máximo 4 dígitos');
      return false;
    }
    // Validar que agência contém apenas números
    if (!/^\d+$/.test(formData.agency)) {
      setError('Agência deve conter apenas números');
      return false;
    }
    if (!formData.account_number) {
      setError('Digite o número da conta');
      return false;
    }
    // Validar limite da conta (máximo 13 dígitos)
    if (formData.account_number.length > 13) {
      setError('Número da conta deve ter no máximo 13 dígitos');
      return false;
    }
    // Validar que conta contém apenas números
    if (!/^\d+$/.test(formData.account_number)) {
      setError('Número da conta deve conter apenas números');
      return false;
    }
    // Validar dígito verificador (se informado)
    if (formData.account_digit && !/^[0-9Xx]{1,2}$/.test(formData.account_digit)) {
      setError('Dígito verificador deve ter 1-2 caracteres (números ou X)');
      return false;
    }

    // Validar PIX (se informado)
    if (formData.pix_key && !formData.pix_type) {
      setError('Selecione o tipo da chave PIX');
      return false;
    }

    return true;
  };

  const handleClose = () => {
    setFormData({
      responsible_id: '',
      bank_name: '',
      bank_code: '',
      account_type: 'corrente',
      agency: '',
      account_number: '',
      account_digit: '',
      pix_key: '',
      pix_type: '',
      holder_name: '',
      holder_document: ''
    });
    setError('');
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      handleClose();
    } catch (err) {
      setError(err.message || 'Erro ao salvar conta bancária');
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
      backdropClassName="modal-backdrop-lightbox"
      className={styles.bankAccountModal}
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          <Icon icon="mdi:bank-plus" className={styles.titleIcon} />
          {editingAccount ? 'Editar Conta Bancária' : 'Cadastrar Conta Bancária'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className={styles.modalBody}>
          {/* Alerta informativo */}
          <Alert variant="info" className="mb-4">
            <Icon icon="mdi:information" /> 
            <strong>Importante:</strong> Sua conta será enviada para aprovação. Após aprovada, você poderá usá-la para saques.
          </Alert>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
              <Icon icon="mdi:alert-circle" /> {error}
            </Alert>
          )}

          {/* Alerta se não houver responsáveis */}
          {responsibles.length === 0 && (
            <Alert variant="warning" className="mb-4">
              <Icon icon="mdi:alert-circle" className="me-2" />
              <strong>Atenção!</strong> Você precisa ter pelo menos um responsável aprovado para cadastrar contas bancárias.
            </Alert>
          )}

          {/* Responsável */}
          <Form.Group className="mb-4">
            <Form.Label className={styles.label}>
              <Icon icon="mdi:account-supervisor" /> Responsável *
            </Form.Label>
            <Form.Select
              value={formData.responsible_id}
              onChange={(e) => handleResponsibleChange(e.target.value)}
              disabled={responsibles.length === 0}
              className={styles.input}
            >
              <option value="">Selecione o responsável</option>
              {responsibles.map((responsible) => (
                <option key={responsible.id} value={responsible.id}>
                  {responsible.company_name || responsible.trading_name || responsible.admin_name || 'Responsável'}
                  {responsible.is_main_responsible && ' (Principal)'}
                  {responsible.responsible_cpf ? ` - ${responsible.responsible_cpf.length > 11 ? 'CNPJ' : 'CPF'}: ${ResponsiblesService.formatCPF(responsible.responsible_cpf)}` : ''}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              A conta bancária será vinculada ao responsável selecionado. Nome e CPF/CNPJ serão preenchidos automaticamente.
            </Form.Text>
          </Form.Group>

          {/* Banco e Tipo de Conta */}
          <div className="row">
            <div className="col-md-8">
              <Form.Group className="mb-4">
                <Form.Label className={styles.label}>
                  <Icon icon="mdi:bank" /> Banco *
                </Form.Label>
                <Form.Select
                  value={formData.bank_code}
                  onChange={(e) => handleBankChange(e.target.value)}
                  className={styles.input}
                >
                  <option value="">Selecione o banco</option>
                  {banks.map((bank) => (
                    <option key={bank.code} value={bank.code}>
                      {bank.code} - {bank.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-4">
                <Form.Label className={styles.label}>
                  <Icon icon="mdi:credit-card" /> Tipo de Conta *
                </Form.Label>
                <Form.Select
                  value={formData.account_type}
                  onChange={(e) => handleChange('account_type', e.target.value)}
                  className={styles.input}
                >
                  <option value="corrente">Conta Corrente</option>
                  <option value="poupanca">Conta Poupança</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          {/* Agência, Conta e Dígito */}
          <div className="row">
            <div className="col-md-4">
              <Form.Group className="mb-4">
                <Form.Label className={styles.label}>
                  <Icon icon="mdi:numeric" /> Agência *
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.agency}
                  onChange={(e) => handleBankFieldChange('agency', e.target.value, 4)}
                  placeholder="Ex: 1234"
                  maxLength={4}
                  inputMode="numeric"
                  className={styles.input}
                />
                <Form.Text className="text-muted">
                  Máximo 4 dígitos, sem o dígito verificador
                </Form.Text>
              </Form.Group>
            </div>
            <div className="col-md-5">
              <Form.Group className="mb-4">
                <Form.Label className={styles.label}>
                  <Icon icon="mdi:numeric" /> Número da Conta *
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.account_number}
                  onChange={(e) => handleBankFieldChange('account_number', e.target.value, 13)}
                  placeholder="Ex: 12345678"
                  maxLength={13}
                  inputMode="numeric"
                  className={styles.input}
                />
                <Form.Text className="text-muted">
                  Apenas números, sem o dígito verificador
                </Form.Text>
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group className="mb-4">
                <Form.Label className={styles.label}>
                  <Icon icon="mdi:numeric" /> Dígito
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.account_digit}
                  onChange={(e) => handleBankFieldChange('account_digit', e.target.value, 2, true)}
                  placeholder="Ex: 6"
                  maxLength={2}
                  className={styles.input}
                />
                <Form.Text className="text-muted">
                  1-2 caracteres
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          {/* Separador PIX */}
          <hr className={styles.divider} />
          <h6 className={styles.sectionTitle}>
            <Icon icon="mdi:qrcode" className="me-2" />
            Configuração PIX (Opcional)
          </h6>

          {/* Tipo e Chave PIX */}
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-4">
                <Form.Label className={styles.label}>
                  <Icon icon="mdi:qrcode" /> Tipo da Chave PIX
                </Form.Label>
                <Form.Select
                  value={formData.pix_type}
                  onChange={(e) => handleChange('pix_type', e.target.value)}
                  className={styles.input}
                >
                  <option value="">Selecione o tipo</option>
                  <option value="cpf">CPF</option>
                  <option value="cnpj">CNPJ</option>
                  <option value="email">E-mail</option>
                  <option value="telefone">Telefone</option>
                  <option value="chave_aleatoria">Chave Aleatória</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-4">
                <Form.Label className={styles.label}>
                  <Icon icon="mdi:key" /> Chave PIX
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.pix_key}
                  onChange={(e) => handleChange('pix_key', e.target.value)}
                  placeholder="Digite a chave PIX"
                  disabled={!formData.pix_type}
                  className={styles.input}
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="light"
            onClick={handleClose}
            disabled={loading}
            className={styles.cancelButton}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '500',
              border: '1px solid rgba(108, 117, 125, 0.2)'
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || responsibles.length === 0}
            className={styles.submitButton}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Salvando...
              </>
            ) : (
              <>
                <Icon icon="mdi:check" className="me-1" />
                {editingAccount ? 'Atualizar Conta' : 'Cadastrar Conta'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BankAccountModal;

